import {
  CrudFilters,
  HttpError,
  useCreate,
  useDelete,
  useOne,
  useTable,
  useTranslation,
} from "@refinedev/core";
import { EyeIcon, PencilIcon, SearchIcon } from "@heroicons/react/outline";
import { CheckCircleIcon, ChevronDownIcon, XCircleIcon } from "@heroicons/react/solid";
import { get, isEmpty } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";
import Button from "components/button";
import { Breadcrumbs } from "components/breadcrumbs";
import { ConfirmModal } from "components/confirmModal";
import { DropdownMenu } from "components/dropdownMenu";
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input";
import { Label } from "components/label";
import Pagination from "components/pagination";
import Select from "components/select";
import { IPickupParticipantItem } from "interfaces";
import { PICKUP_STATUS } from "utills/constants";
import { isHaveActionPermission } from "utills/helpers";
import { getConfig, PAGE_SIZE } from "../config";
import { getListLabels } from "../labels";
import {
  ParticipantEndpointConfig,
  ParticipantItem,
  PickupParticipantKind,
  PickupParticipantVariant,
} from "../types";
import { getMessage, getParticipantId } from "../utils";

const SharedFilter = ({
  config,
  kind,
  variant,
  currentFilterValues,
  setFilters,
  onDefaultEventApplied,
}: {
  config: ParticipantEndpointConfig;
  kind: PickupParticipantKind;
  variant: PickupParticipantVariant;
  currentFilterValues: any;
  setFilters: (filters: CrudFilters) => void;
  onDefaultEventApplied?: () => void;
}) => {
  const { translate } = useTranslation();
  const labels = getListLabels(kind, variant, translate);
  const [keyword, setKeyword] = useState(currentFilterValues.search);
  const [eventId, setEventId] = useState(currentFilterValues.event_id);
  const [categoryId, setCategoryId] = useState(currentFilterValues.category);
  const [status, setStatus] = useState(currentFilterValues.pickup_status);
  const hasSetDefaultEvent = useRef(false);

  const { data: eventsData, isFetching: isFetchingEvents } = useOne<any[], HttpError>({
    resource: config.eventsResource,
    id: "",
    meta: {
      resource: "data",
    },
  });

  const { data: categoryData, isFetching: isFetchingCategories } = useOne<string[], HttpError>({
    resource: config.categoriesResource(eventId),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventId,
    } as any,
  });

  const eventOptions = (eventsData?.data ?? []).map((event: any) => ({
    value: event.id,
    label: event.name ?? event.name_vi ?? event.name_en,
  }));
  const categoryOptions = (!isEmpty(eventId) ? categoryData?.data ?? [] : []).map((category: any) => {
    const categoryName =
      typeof category === "string"
        ? category
        : get(category, "category_en", get(category, "category_vi", get(category, "name", "")));

    return {
      value: categoryName,
      label: categoryName,
    };
  });
  const statusOptions =
    variant === "wps"
      ? [
          { value: "true", label: translate("checkin.completed") },
          { value: "false", label: translate("checkin.incomplete") },
        ]
      : PICKUP_STATUS;

  useEffect(() => {
    if (variant !== "wps" || hasSetDefaultEvent.current || currentFilterValues.event_id || eventId) return;

    const defaultEventId = get(eventsData, "data[0].id", "");
    if (!defaultEventId) return;

    hasSetDefaultEvent.current = true;
    setEventId(defaultEventId);
    setCategoryId("");
    setFilters([
      { field: "search", operator: "contains", value: keyword },
      { field: "event_id", operator: "eq", value: defaultEventId },
      { field: "pickup_status", operator: "eq", value: status },
      { field: "category", operator: "eq", value: "" },
      { field: "page", operator: "eq", value: 1 },
    ]);
    onDefaultEventApplied?.();
  }, [
    currentFilterValues.event_id,
    eventId,
    eventsData,
    keyword,
    onDefaultEventApplied,
    setFilters,
    status,
    variant,
  ]);

  const submitFilter = () => {
    setFilters([
      { field: "search", operator: "contains", value: keyword },
      { field: "event_id", operator: "eq", value: eventId },
      { field: "pickup_status", operator: "eq", value: status },
      { field: "category", operator: "eq", value: categoryId },
      { field: "page", operator: "eq", value: 1 },
    ]);
  };

  const formClass =
    variant === "wps"
      ? "box-container w-full flex flex-col gap-3"
      : "w-full flex flex-col gap-y-4";
  const filterRowClass =
    variant === "wps"
      ? "w-full flex flex-col sm:flex-row items-end gap-3"
      : "w-full flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3";
  const searchButtonClass =
    variant === "wps"
      ? "w-full mt-2 sm:mt-0 sm:w-[120px]"
      : "w-full md:w-auto";

  return (
    <form
      className={formClass}
      onSubmit={(event) => {
        event.preventDefault();
        submitFilter();
      }}
    >
      <div className={filterRowClass}>
        <div className="w-full flex-1">
          <Label id="search" name={labels.search} />
          <div className="mt-2">
            <Input
              className="px-2 py-2"
              id="search"
              type="text"
              placeholder={labels.searchPlaceholder}
              value={keyword}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setKeyword(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === "Enter") submitFilter();
              }}
            />
          </div>
        </div>
        <div className="w-full flex-1">
          <Label id="event_id" name={labels.events} />
          <div className="mt-2">
            <Select
              options={eventOptions}
              isLoading={isFetchingEvents}
              isClearable={true}
              id="event_id"
              placeholder={labels.eventPlaceholder}
              className="py-0.5"
              value={eventOptions.find((item) => item.value === eventId)}
              onChange={(selected: { label: string; value: string }) => {
                setEventId(selected?.value ?? "");
                setCategoryId("");
              }}
            />
          </div>
        </div>
      </div>
      <div className={filterRowClass}>
        <div className="w-full flex-1">
          <Label id="category" name={labels.category} />
          <div className="mt-2">
            <Select
              options={categoryOptions}
              isLoading={isFetchingCategories}
              isClearable={true}
              key={`category__${eventId ?? "all"}`}
              id="category"
              placeholder={labels.categoryPlaceholder}
              className="py-0.5"
              value={categoryOptions.find((item) => item.value === categoryId)}
              onChange={(selected: { label: string; value: string }) => setCategoryId(selected?.value ?? "")}
            />
          </div>
        </div>
        <div className="w-full flex-1">
          <Label id="pickup_status" name={labels.pickupStatus} />
          <div className="mt-2">
            <Select
              options={statusOptions}
              isClearable={true}
              id="pickup_status"
              placeholder={labels.pickupStatusPlaceholder}
              className="py-0.5"
              value={statusOptions.find((item) => item.value === status)}
              onChange={(selected: { label: string; value: string }) => setStatus(selected?.value ?? "")}
            />
          </div>
        </div>
        <Button
          type="submit"
          className={`${searchButtonClass} bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85`}
        >
          <SearchIcon className="w-4 h-4 text-brand-500 mr-2" /> {labels.search}
        </Button>
      </div>
    </form>
  );
};

const ParticipantStatus = ({ checked }: { checked: any }) =>
  checked ? (
    <CheckCircleIcon className="size-6 text-green-500" />
  ) : (
    <XCircleIcon className="size-6 text-red-500" />
  );

const ParticipantTable = ({
  config,
  kind,
  variant,
  data,
  pageSize,
  currentPage,
  participantIds,
  setParticipantIds,
  checkParticipantId,
  undoCheckParticipantId,
  fetchParticipantIds,
  renderActionButtons,
}: {
  config: ParticipantEndpointConfig;
  kind: PickupParticipantKind;
  variant: PickupParticipantVariant;
  data: ParticipantItem[];
  pageSize: number;
  currentPage: number;
  participantIds: string[] | null;
  setParticipantIds: (ids: string[] | null) => void;
  checkParticipantId: (id: string) => void;
  undoCheckParticipantId: (id: string) => void;
  fetchParticipantIds: () => void;
  renderActionButtons: (isMobile: boolean) => JSX.Element;
}) => {
  const { translate } = useTranslation();
  const labels = getListLabels(kind, variant, translate);
  const isSport = kind === "sport";
  const canBulkAction =
    isHaveActionPermission(config.permissionResource, config.permissionApp, "export") ||
    isHaveActionPermission(config.permissionResource, config.permissionApp, "delete");
  const isAllChecked = !isEmpty(participantIds);

  if (variant === "admin") {
    return (
      <DesktopTable
        config={config}
        kind={kind}
        labels={labels}
        data={data}
        pageSize={pageSize}
        currentPage={currentPage}
        participantIds={participantIds}
        setParticipantIds={setParticipantIds}
        checkParticipantId={checkParticipantId}
        undoCheckParticipantId={undoCheckParticipantId}
        fetchParticipantIds={fetchParticipantIds}
        canBulkAction={canBulkAction}
      />
    );
  }

  return (
    <section className="flex flex-col gap-5">
      <div className="block sm:hidden">
        {canBulkAction ? (
          <div className="mb-5 w-full flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Input
                type="checkbox"
                checked={isAllChecked}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) fetchParticipantIds();
                  else setParticipantIds([]);
                }}
              />
              <Label className="mb-0" id="select_all" name={labels.selectAll} />
            </div>
            {renderActionButtons(true)}
          </div>
        ) : null}

        <div className="mb-6 w-full flex flex-col gap-[8px]">
          {data.map((item: any, index: number) => {
            const participantId = getParticipantId(item);
            return (
              <div key={participantId || index} className="w-full bg-white p-[16px] rounded-[8px] shadow-md">
                <div className="w-full flex items-center justify-between">
                  {canBulkAction ? (
                    <Input
                      type="checkbox"
                      checked={(participantIds ?? []).includes(participantId)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        if (event.target.checked) checkParticipantId(participantId);
                        else undoCheckParticipantId(participantId);
                      }}
                    />
                  ) : (
                    <div className="opacity-0">{labels.action}</div>
                  )}
                  <div className="flex items-center gap-2">
                    {isHaveActionPermission(config.permissionResource, config.permissionApp, "view") ? (
                      <Link to={config.detailPath(participantId)}>
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <EyeIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : null}
                    {isHaveActionPermission(config.permissionResource, config.permissionApp, "edit") ? (
                      <Link to={config.editPath(participantId)}>
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <PencilIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                  {[
                    [labels.no, String((currentPage - 1) * pageSize + 1 + index)],
                    [labels.participantId, participantId, config.detailPath(participantId)],
                    [labels.fullName, get(item, "full_name", "")],
                    [labels.category, get(item, "category", "")],
                    ...(isSport ? [[labels.bib, get(item, "bib", "")]] : []),
                    [labels.email, get(item, "email", "")],
                    [labels.phone, get(item, "phone", "")],
                    ...(isSport
                      ? [[labels.idCard, get(item, "id_card", "")]]
                      : [
                          [labels.orderCode, get(item, "order_code", ""), `/orders/${get(item, "order_code", "")}`],
                          [labels.ticketNo, get(item, "ticket_no", "")],
                        ]),
                    [labels.eventName, get(item, "event_name", "")],
                  ].map(([label, value, href]) => (
                    <div className="w-full flex" key={label as string}>
                      <span className="w-[30%] text-[14px] font-[500] text-[#262626]">{label}</span>
                      <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                        {href ? (
                          <Link to={href as string} className="w-fit text-brand-500 hover:underline">
                            {value}
                          </Link>
                        ) : (
                          value
                        )}
                      </span>
                    </div>
                  ))}
                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">{labels.pickupStatus}</span>
                    <div className="w-full flex justify-end">
                      <ParticipantStatus checked={get(item, "pos", "")} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden sm:block">
        <DesktopTable
          config={config}
          kind={kind}
          labels={labels}
          data={data}
          pageSize={pageSize}
          currentPage={currentPage}
          participantIds={participantIds}
          setParticipantIds={setParticipantIds}
          checkParticipantId={checkParticipantId}
          undoCheckParticipantId={undoCheckParticipantId}
          fetchParticipantIds={fetchParticipantIds}
          canBulkAction={canBulkAction}
        />
      </div>
    </section>
  );
};

const DesktopTable = ({
  config,
  kind,
  labels,
  data,
  pageSize,
  currentPage,
  participantIds,
  setParticipantIds,
  checkParticipantId,
  undoCheckParticipantId,
  fetchParticipantIds,
  canBulkAction,
}: any) => {
  const isSport = kind === "sport";

  return (
    <div className="flow-root">
      <div className="relative overflow-x-auto min-w-full">
        <table className="mb-6 table-auto w-max divide-y divide-gray-300 bg-magnolia-500 rounded-md min-w-full">
          <thead>
            <tr>
              <th scope="col" className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8">
                {labels.no}
              </th>
              <th scope="col" className="th">
                <Input
                  type="checkbox"
                  disabled={!canBulkAction}
                  checked={!isEmpty(participantIds)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.target.checked) fetchParticipantIds();
                    else setParticipantIds([]);
                  }}
                />
              </th>
              <th scope="col" className="th">{labels.participantId}</th>
              <th scope="col" className="th">{labels.fullName}</th>
              <th scope="col" className="th">{labels.category}</th>
              {isSport ? <th scope="col" className="th">{labels.bib}</th> : null}
              <th scope="col" className="th">{labels.email}</th>
              <th scope="col" className="th">{labels.phone}</th>
              {isSport ? (
                <th scope="col" className="th">{labels.idCard}</th>
              ) : (
                <>
                  <th scope="col" className="th">{labels.orderCode}</th>
                  <th scope="col" className="th">{labels.ticketNo}</th>
                </>
              )}
              <th scope="col" className="th">{labels.eventName}</th>
              <th scope="col" className="th">{labels.pickupStatus}</th>
              <th scope="col" className="th w-28">
                <span className="sr-only">{labels.action}</span>
                <p>{labels.action}</p>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((participant: IPickupParticipantItem, index: number) => {
              const participantId = getParticipantId(participant);
              return (
                <tr key={participantId || index}>
                  <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                    {(currentPage - 1) * pageSize + 1 + index}
                  </td>
                  <td className="td">
                    <Input
                      type="checkbox"
                      disabled={!canBulkAction}
                      checked={(participantIds ?? []).includes(participantId)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        if (event.target.checked) checkParticipantId(participantId);
                        else undoCheckParticipantId(participantId);
                      }}
                    />
                  </td>
                  <td className="td min-w-[230px]">
                    <Link to={config.detailPath(participantId)} className="w-fit text-brand-500 hover:underline">
                      {participantId}
                    </Link>
                  </td>
                  <td className="td min-w-[230px]">{get(participant, "full_name", "")}</td>
                  <td className="td min-w-[100px]">{get(participant, "category", "")}</td>
                  {isSport ? <td className="td min-w-[100px]">{get(participant, "bib", "")}</td> : null}
                  <td className="td">{get(participant, "email", "")}</td>
                  <td className="td min-w-[150px]">{get(participant, "phone", "")}</td>
                  {isSport ? (
                    <td className="td min-w-[150px]">{get(participant, "id_card", "")}</td>
                  ) : (
                    <>
                      <td className="td min-w-[120px]">
                        <Link to={`/orders/${get(participant, "order_code", "")}`} className="w-fit font-medium text-brand-500 hover:underline">
                          {get(participant, "order_code", "")}
                        </Link>
                      </td>
                      <td className="td min-w-[150px]">{get(participant, "ticket_no", "")}</td>
                    </>
                  )}
                  <td className="td min-w-[230px]">{get(participant, "event_name", "")}</td>
                  <td className="td min-w-[120px]">
                    <ParticipantStatus checked={get(participant, "pos", "")} />
                  </td>
                  <td className="td min-w-[100px] py-4 pl-3 pr-4 sm:pr-6 lg:pr-8 flex items-center gap-2">
                    {isHaveActionPermission(config.permissionResource, config.permissionApp, "view") ? (
                      <Link to={config.detailPath(participantId)} className="w-fit">
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <EyeIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : null}
                    {isHaveActionPermission(config.permissionResource, config.permissionApp, "edit") ? (
                      <Link to={config.editPath(participantId)} className="w-fit">
                        <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                          <PencilIcon className="w-4 h-4 text-black" />
                        </Button>
                      </Link>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const PickupParticipantListPage = ({
  kind,
  variant,
}: {
  kind: PickupParticipantKind;
  variant: PickupParticipantVariant;
}) => {
  const config = getConfig(kind, variant);
  const { translate } = useTranslation();
  const labels = getListLabels(kind, variant, translate);
  const [participantIds, setParticipantIds] = useState<string[] | null>([]);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    open: false,
    title: "",
    submit: () => {},
  });
  const [exportId, setExportId] = useState<string | null>();
  const [searchParams] = useSearchParams();
  const hasParams = searchParams.toString() !== "";
  const hasEventIdParam = Array.from(searchParams.entries()).some(
    ([key, value]) => key.includes("event_id") || value === "event_id",
  );
  const [isWpsListReady, setIsWpsListReady] = useState(variant !== "wps" || hasEventIdParam);
  const isListQueryEnabled = variant === "wps" ? isWpsListReady : hasParams;
  const { mutate: postActionMutation, isLoading: createMutationIsLoading } = useCreate();
  const { mutate: deleteMutation, isLoading: deleteMutationIsLoading } = useDelete();

  const { tableQuery, filters, setFilters } = useTable<ParticipantItem>({
    resource: config.listResource,
    syncWithLocation: true,
    pagination: {
      mode: "off",
      pageSize: PAGE_SIZE,
    },
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: isListQueryEnabled,
    } as any,
  });

  const { isFetching: isFetchingParticipantIdsData, refetch: fetchParticipantIds } = useOne<string[], HttpError>({
    resource: config.idsResource,
    id: "",
    meta: {
      resource: "data",
      filters: config.idsFilters(filters),
    },
    queryOptions: {
      enabled: false,
    } as any,
    successNotification: (data, values, resource) => {
      setParticipantIds((data?.data ?? []) as string[]);
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  const { refetch: checkStatus } = useOne({
    resource: config.exportResultResource,
    id: exportId ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
    errorNotification: () => {
      setExportId(null);
      return {
        message: "Export Error",
        description: "Export Error",
        type: "error",
      };
    },
  });

  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        checkStatus().then((data) => {
          const isSuccess = get(data, "data.data.is_success", false);
          if (isSuccess) {
            setExportId(null);
            window.location.href = get(data, "data.data.file_link", "#");
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [exportId, checkStatus]);

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) => ("field" in item ? item : []));

    return {
      search: logicalFilters.find((item) => item.field === "search")?.value || "",
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
      category: logicalFilters.find((item) => item.field === "category")?.value || "",
      pickup_status: logicalFilters.find((item) => item.field === "pickup_status")?.value || "",
    };
  }, [filters]);

  const handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap((item) => ("field" in item ? item : []));
    const pageIndex = updatedFilters.findIndex((filter) => filter.field === "page");
    if (pageIndex < 0) updatedFilters.push({ field: "page", operator: "eq", value: page });
    else updatedFilters[pageIndex].value = page;
    setFilters(updatedFilters);
  };

  const exportParticipants = (ids: string[], isInternal = true) => {
    postActionMutation(
      {
        resource: isInternal ? config.exportResource : config.exportPartnerResource,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          participant_ids: ids,
          ...(variant === "admin" ? { is_internal: isInternal } : {}),
        },
      },
      {
        onSuccess: (res) => {
          if (variant === "wps") {
            toast.success(translate("export-success"));
            return;
          }

          const nextExportId = get(res, "data.data.export_id", "");
          setExportId(nextExportId);
        },
      },
    );
  };

  const deleteParticipants = (ids: string[]) => {
    deleteMutation(
      {
        resource: config.deleteResource,
        id: "delete",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: config.deleteValues(ids),
      },
      {
        onSuccess: (res) => {
          toast.success(getMessage(get(res, "data.message"), undefined, "Deleted successfully"));
          setParticipantIds([]);
          tableQuery.refetch();
        },
      },
    );
  };

  const renderActionButtons = (isMobile: boolean) =>
    !isEmpty(participantIds) ? (
      <div className={`shrink-0 ${variant === "admin" ? "block" : isMobile ? "block sm:hidden" : "hidden sm:block"}`}>
        <DropdownMenu
          position="bottom"
          label={
            <Button className="w-fit bg-brand-500 rounded-md text-white font-bold border-solid border-brand-500 border-[1px] active:opacity-85">
              {labels.moreActions}
              <ChevronDownIcon className="ml-2 size-4 text-white" />
            </Button>
          }
          items={[
            ...(isHaveActionPermission(config.permissionResource, config.permissionApp, "export")
              ? [
                  {
                    label: labels.download,
                    onClick: () => exportParticipants(participantIds ?? []),
                  },
                  {
                    label: labels.downloadForPartner,
                    onClick: () => exportParticipants(participantIds ?? [], false),
                  },
                ]
              : []),
            ...(isHaveActionPermission(config.permissionResource, config.permissionApp, "delete")
              ? [
                  {
                    label: labels.delete,
                    onClick: () =>
                      setConfirmModalConfig({
                        title: labels.deleteConfirm((participantIds ?? []).length),
                        open: true,
                        submit: () => deleteParticipants(participantIds ?? []),
                      }),
                  },
                ]
              : []),
          ]}
        />
      </div>
    ) : (
      <></>
    );

  const filterNode = (
    <SharedFilter
      config={config}
      kind={kind}
      variant={variant}
      currentFilterValues={currentFilterValues}
      setFilters={(data) => {
        setFilters(data);
        setParticipantIds([]);
      }}
      onDefaultEventApplied={() => setIsWpsListReady(true)}
    />
  );

  const tableNode = (
    <div className="box-container w-full min-w-0 max-w-full overflow-hidden">
      {isListQueryEnabled && !tableQuery.data?.data?.length && !tableQuery.isFetching ? (
        <div className="text-center">{labels.noData}</div>
      ) : null}

      {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
        <>
          <ParticipantTable
            config={config}
            kind={kind}
            variant={variant}
            data={tableQuery.data.data as ParticipantItem[]}
            pageSize={tableQuery.data.pageSize}
            currentPage={tableQuery.data.paging?.current_page ?? 1}
            participantIds={participantIds}
            setParticipantIds={setParticipantIds}
            checkParticipantId={(id) => {
              if ((participantIds ?? []).includes(id)) return;
              setParticipantIds([...(participantIds ?? []), id]);
            }}
            undoCheckParticipantId={(id) =>
              setParticipantIds([...(participantIds ?? [])].filter((participantId) => participantId !== id))
            }
            fetchParticipantIds={fetchParticipantIds}
            renderActionButtons={renderActionButtons}
          />
          {tableQuery?.data?.paging ? (
            <div className="mt-6">
              <Pagination
                currentPage={tableQuery.data.paging.current_page}
                totalPage={tableQuery.data.paging.total_page}
                total={{
                  totalItems: tableQuery.data.paging?.total_item ?? 0,
                  total: tableQuery.data.paging.totals ?? tableQuery.data.paging.total,
                }}
                onChange={handlePageChanged}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );

  return (
    <div className={variant === "wps" ? "w-full min-w-0 max-w-full overflow-hidden" : "wrapper"}>
      <Breadcrumbs
        type={variant === "wps" ? "wps" : undefined}
        breadcrumbItems={
          variant === "wps"
            ? [
                { label: labels.checkIn, href: config.basePath },
                { label: labels.pageTitle, href: config.basePath },
              ]
            : undefined
        }
        disableBorder={variant === "wps" ? true : undefined}
        additionalLabel={
          <div className="flex items-center justify-between py-0">
            {isHaveActionPermission(config.permissionResource, config.permissionApp, "export") ||
            isHaveActionPermission(config.permissionResource, config.permissionApp, "delete")
              ? renderActionButtons(false)
              : null}
          </div>
        }
      />
      <div className={variant === "wps" ? "w-full min-w-0 max-w-full overflow-hidden p-[16px] flex flex-col gap-[16px]" : "w-full flex flex-col gap-y-4"}>
        {variant === "admin" ? <div className="box-container">{filterNode}</div> : filterNode}
        {tableNode}
      </div>
      {(tableQuery.isLoading && isListQueryEnabled) ||
      (tableQuery.isFetching && tableQuery.isPreviousData) ||
      isFetchingParticipantIdsData ||
      createMutationIsLoading ||
      deleteMutationIsLoading ||
      exportId ? (
        <FullScreenLoading />
      ) : null}
      <ConfirmModal
        key="confirm-action"
        title={confirmModalConfig.title}
        content=""
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={() =>
          setConfirmModalConfig({
            open: false,
            title: confirmModalConfig.title,
            submit: () => {},
          })
        }
      />
    </div>
  );
};
